// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Link } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Email from "@werner/XDL/Icons/Email";
import PhoneLine from "@werner/XDL/Icons/PhoneLine";
import SocialLi from "@werner/XDL/Icons/SocialLi";
import { color, createLinkIconStyles, xMask, xScreen } from "@werner/XDL/Utilities";
import { validators } from "@werner/XDL/Utilities/form";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    name = "name",
    title = "title",
    description = "description",
    email = "email",
    phone = "phone",
    linkedInUrlText = "linkedInUrlText",
    linkedInUrl = "linkedInUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.name]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.email]: string;
        [fields.phone]: string;
        [fields.linkedInUrlText]: string;
        [fields.linkedInUrl]: LinkFieldValue;
    };
}

type Props = OwnProps;

interface ContactInformationStyles {
    container?: InjectableCss;
    cardHeading?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    infoWrapper?: InjectableCss;
    name?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    emailLink?: LinkPresentationProps;
    phoneLink?: LinkPresentationProps;
    linkedInLink?: LinkPresentationProps;
}

const linkIconStyles: LinkPresentationProps = {
    color: "primary",
    typographyProps: {
        size: 14,
        weight: 500,
        css: css`
            letter-spacing: 0.5px;
            line-height: 16px;
            &:hover {
                color: ${color("common.ui3")};
            }
        `,
    },
    icon: {
        iconProps: {
            size: 14,
        },
    },
};

const contactInformationStyles: ContactInformationStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 20px 32px;
            height: 280px;
            width: 520px;
            border-top: 10px solid ${color("primary")};
            box-shadow: 0 0 15px 0 ${color("primary", 0.15)};
        `,
    },
    cardHeading: {
        size: 28,
        lineHeight: "32px",
        weight: 500,
        css: css`
            margin-bottom: 6px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            gap: 16px;
            align-items: center;
            margin-top: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 120px;
            height: 120px;
            margin-left: 30px;
        `,
    },
    image: {
        css: css`
            height: auto;
            width: auto;
            object-fit: contain;
            max-height: 100%;
            max-width: 100%;
            border-radius: 50%;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    name: {
        size: 18,
        lineHeight: "24px",
        weight: 500,
    },
    title: {
        size: 15,
        lineHeight: "18px",
        weight: 500,
    },
    description: {
        css: css`
            margin-bottom: 6px;
        `,
    },
    emailLink: {
        ...createLinkIconStyles(Email, linkIconStyles),
    },
    phoneLink: {
        ...createLinkIconStyles(PhoneLine, linkIconStyles),
    },
    linkedInLink: {
        ...createLinkIconStyles(SocialLi, linkIconStyles),
    },
};

const ContactInformation: React.FC<Props> = ({ fields, id }) => {
    const styles = contactInformationStyles;
    const link = useGetLink(fields.linkedInUrl);
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.cardHeading}>{translate("Contact Information")}</Typography>
            <Typography {...styles.description}>
                {parse(fields.description, parserOptions)}
            </Typography>
            <StyledWrapper {...styles.wrapper}>
                {fields.image && (
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage src={fields.image} altText={fields.name} {...styles.image} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.infoWrapper}>
                    <Typography {...styles.name}>{fields.name}</Typography>
                    <Typography {...styles.title}>{fields.title}</Typography>
                    {fields.email && (
                        <Link {...styles.emailLink} href={`mailto:${fields.email}`} aria-label={`Contact Email Address: ${fields.email}`}>
                            {fields.email}
                        </Link>
                    )}
                    {fields.phone && (
                        <Link {...styles.phoneLink} href={`tel:${fields.phone}`} aria-label={`Contact Phone: ${fields.phone}`}>
                            {xMask.phoneNumber(fields.phone)}
                        </Link>
                    )}
                    {link.url && (
                        <Link {...styles.linkedInLink} href={link.url} aria-label="Contact Url">
                            {fields.linkedInUrlText || link.title}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContactInformation,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Shared Contact Information",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Image",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.name,
                editorTemplate: "TextField",
                displayName: "Name",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.email,
                editorTemplate: "TextField",
                displayName: "Email",
                defaultValue: "",
                isRequired: true,
                validate: (value: string | null, item: HasFields): string | null => {
                    if (!value) {
                        return `${siteMessage("Custom_Field_Required")}`;
                    }
                    const validtedInput = validators.email(value);
                    return validtedInput === false ? null : `${siteMessage("Custom_EmailAddress_ValidEmail")}`;
                },
            },
            {
                fieldType: "General",
                name: fields.phone,
                editorTemplate: "TextField",
                displayName: "Phone",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.linkedInUrlText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.linkedInUrl,
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
